import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from '@services/api/client';

/** Surfaces the grounded "Explain this" supports (mirrors ExplainService::SURFACES). */
export type ExplainSurface =
  | 'overview'
  | 'anomaly'
  | 'executions'
  | 'governance'
  | 'incident'
  | 'integration';

export const ExplainResultSchema = z.object({
  surface: z.string(),
  label: z.string(),
  analysis: z.string(),
  // True when the LLM was unreachable and `analysis` is the fallback sentence, so the UI can
  // show a retry affordance instead of presenting the outage line as a real grounded answer.
  degraded: z.boolean().optional(),
});
export type ExplainResult = z.infer<typeof ExplainResultSchema>;

/**
 * Grounded LLM analysis of a surface's real data. POST (not cached) so each
 * "Explain this" is a fresh call against the live data + gateway.
 */
export function useExplain(): UseMutationResult<ExplainResult, Error, ExplainSurface> {
  return useMutation({
    mutationFn: (surface: ExplainSurface) =>
      apiClient.request({
        path: '/v2/intelligence/explain',
        method: 'POST',
        body: { surface },
        schema: ExplainResultSchema,
        timeoutMs: 65_000, // above the server's 60s LLM cap — don't abort a live call
      }),
  });
}
